import type { CampaignNumberPreviewQuery } from '@ooh/contracts';
import { isBrandStillValid } from './brand-inline-client';

export interface CampaignNumberPreviewSelection {
  clientId: number | null;
  clientAcronym: string | null;
  /** The selected client's brands, or undefined when no client is selected. */
  clientBrands: string[] | undefined;
  brand: string | null | undefined;
  salespersonName: string | null;
}

/**
 * Composes the campaign-number preview's query input from a form's current
 * client/brand/salesperson selection (offer-draft-numbering /
 * "Campaign-number preview on offer creation forms"). Shared by
 * create-offer-page.tsx and quick-add-offer-picker-form.ts — both forms
 * follow the exact same rule, so it lives in exactly one place (PR #1141
 * review finding 3, DRY violation).
 *
 * `null` until both a client and a salesperson are selected, matching the
 * "no preview requested" incomplete state.
 *
 * Discards `brand` via `isBrandStillValid` rather than trusting it verbatim:
 * right after switching clients, the watched `brand` form field still holds
 * the previous client's value for one render — the field-clearing effect
 * that resets it to null runs afterward. Composing straight from the raw
 * value would momentarily pair the newly selected client's acronym with a
 * brand that client doesn't have (PR #1141 review finding 2). Validating
 * against `clientBrands` here makes the composed input correct on every
 * render, independent of when that effect fires.
 */
export function computeCampaignNumberPreviewInput(
  selection: CampaignNumberPreviewSelection,
): CampaignNumberPreviewQuery | null {
  const { clientId, clientAcronym, clientBrands, brand, salespersonName } = selection;
  if (clientId === null || clientAcronym === null || salespersonName === null) return null;

  const effectiveBrand = isBrandStillValid(brand, clientBrands) ? brand : null;

  return {
    clientId,
    acronym: clientAcronym,
    salespersonName,
    ...(effectiveBrand ? { brand: effectiveBrand } : {}),
  };
}
